import { useTranslation } from 'react-i18next';

import { Button } from '@shared/components/ui/Button';
import { isChunkLoadError, reloadForStaleBuild } from '@shared/lib/chunk-recovery';

interface ErrorFallbackProps {
  error?: Error;
  resetErrorBoundary?: () => void;
}

export function ErrorFallback({ error, resetErrorBoundary }: ErrorFallbackProps) {
  const { t } = useTranslation('errors');
  const staleBuild = isChunkLoadError(error);

  return (
    <div className="flex flex-col items-center justify-center gap-4 p-8" role="alert">
      <h2 className="text-lg font-semibold text-destructive">
        {staleBuild
          ? t('appUpdated', 'A new version is available')
          : t('somethingWentWrong', 'Something went wrong')}
      </h2>

      {staleBuild ? (
        <p className="max-w-lg text-center text-sm text-muted-foreground">
          {t(
            'appUpdatedDesc',
            'This page stayed open while the app was updated. Reload to continue.',
          )}
        </p>
      ) : (
        error && (
          <pre className="max-w-lg overflow-auto rounded-md bg-muted p-4 text-sm">
            {error.message}
          </pre>
        )
      )}

      <div className="flex items-center gap-2">
        {staleBuild ? (
          <Button
            onClick={() => {
              reloadForStaleBuild({ force: true });
            }}
          >
            {t('reloadPage', 'Reload page')}
          </Button>
        ) : (
          <>
            {resetErrorBoundary && (
              <Button onClick={resetErrorBoundary}>{t('tryAgain', 'Try again')}</Button>
            )}
            <Button
              variant="outline"
              onClick={() => {
                reloadForStaleBuild({ force: true });
              }}
            >
              {t('reloadPage', 'Reload page')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
